import React, { useState, useRef } from 'react';
import { Upload, X, File, Image, Video } from 'lucide-react';

export function FileUpload({ onFilesChange, maxFiles = 5, maxSize = 10 * 1024 * 1024, acceptedTypes = ['image/*', 'video/*', 'application/pdf', '.doc', '.docx', '.txt', '.csv'] }) {
    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(',')[1]; // Remove data:mime/type;base64, prefix
                resolve({
                    name: file.name,
                    data: base64String,
                    mimeType: file.type,
                    size: file.size,
                    uploadedAt: new Date().toISOString()
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleFiles = async (newFiles) => {
        const fileArray = Array.from(newFiles);
        const validFiles = fileArray.filter(file => {
            // Check file size
            if (file.size > maxSize) {
                alert(`File ${file.name} is too large. Maximum size is ${maxSize / (1024 * 1024)}MB`);
                return false;
            }

            // Check file type if specified
            if (acceptedTypes.length > 0) {
                const fileType = file.type;

                const isValidType = acceptedTypes.some(type => {
                    if (type.startsWith('.')) {
                        return file.name.toLowerCase().endsWith(type.toLowerCase());
                    }
                    // Handle wildcard types like image/*, video/*
                    if (type.includes('/*')) {
                        const baseType = type.split('/')[0];
                        return fileType.startsWith(baseType + '/');
                    }
                    return fileType.includes(type);
                });

                if (!isValidType) {
                    alert(`File ${file.name} is not an accepted type. Accepted types: ${acceptedTypes.join(', ')}`);
                    return false;
                }
            }

            return true;
        });

        try {
            // Convert valid files to base64
            const base64Files = await Promise.all(validFiles.map(convertFileToBase64));

            const updatedFiles = [...files, ...base64Files].slice(0, maxFiles);

            // Calculate total size and warn if too large
            const totalSize = updatedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
            const totalSizeMB = totalSize / (1024 * 1024);

            if (totalSizeMB > 40) { // Warn at 40MB (leave some buffer for 50MB limit)
                alert(`Warning: Total file size is ${totalSizeMB.toFixed(1)}MB. This may cause upload issues. Consider reducing file sizes.`);
            }

            setFiles(updatedFiles);
            onFilesChange(updatedFiles);
        } catch (error) {
            console.error('Error converting files to base64:', error);
            alert('Error processing files. Please try again.');
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFiles(e.target.files);
        }
    };

    const removeFile = (index) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        onFilesChange(updatedFiles);
    };

    const getFileIcon = (file) => {
        const mimeType = file.mimeType || file.type;
        if (mimeType.startsWith('image/')) {
            return <Image className="h-4 w-4" />;
        } else if (mimeType.startsWith('video/')) {
            return <Video className="h-4 w-4" />;
        } else {
            return <File className="h-4 w-4" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="w-full">
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                    }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInput}
                    accept={acceptedTypes.join(',')}
                    className="hidden"
                />

                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-1">
                    Drag and drop files here, or{' '}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        browse
                    </button>
                </p>
                <p className="text-xs text-gray-500">
                    Max {maxFiles} files, {formatFileSize(maxSize)} each
                    {acceptedTypes.length > 0 && `, types: ${acceptedTypes.join(', ')}`}
                </p>
            </div>

            {files.length > 0 && (
                <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Selected Files:</h4>
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                {getFileIcon(file)}
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
